<template>
    <header
        v-if="selectedRoomInfo"
        class="chat_header"
        :class="{
            'chat_header--group': isGroupChat,
            'chat_header--blocked': isBlocked
        }"
    >
        <div class="chat_header__inner">
            <button
                v-if="isMobile"
                type="button"
                class="chat_header__icon_button chat_header__back"
                aria-label="Back to conversations"
                title="Back"
                @click="$emit('back')"
            >
                <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    <path
                        d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H20v-2z"
                    />
                </svg>
            </button>

            <button
                type="button"
                class="chat_header__identity"
                :aria-label="identityActionLabel"
                @click="handleIdentityClick"
            >
                <span class="chat_header__avatar">
                    <img
                        v-if="avatarSource"
                        :src="avatarSource"
                        :alt="displayName"
                    >

                    <span
                        v-else
                        class="chat_header__initials"
                        aria-hidden="true"
                    >
                        {{ initials }}
                    </span>

                    <span
                        v-if="!isGroupChat"
                        class="chat_header__presence"
                        :class="presenceClass"
                        :title="presenceStatus.label"
                    />
                </span>

                <span class="chat_header__details">
                    <span class="chat_header__name">
                        {{ displayName }}
                    </span>

                    <span
                        class="chat_header__status"
                        :class="statusClass"
                    >
                        {{ subtitle }}
                    </span>
                </span>
            </button>

            <div class="chat_header__actions">
                <button
                    type="button"
                    class="chat_header__icon_button"
                    aria-label="Search messages"
                    title="Search messages"
                    :disabled="disabled"
                    @click="$emit('search')"
                >
                    <svg viewBox="0 0 24 24" height="26" width="26" preserveAspectRatio="xMidYMid meet" version="1.1" x="0px"
                        y="0px" enable-background="new 0 0 24 24" xml:space="preserve">
                        <path fill="currentcolor"
                            d="M15.9,14.3H15L14.7,14c1-1.1,1.6-2.7,1.6-4.3c0-3.7-3-6.7-6.7-6.7S3,6,3,9.7 s3,6.7,6.7,6.7c1.6,0,3.2-0.6,4.3-1.6l0.3,0.3v0.8l5.1,5.1l1.5-1.5L15.9,14.3z M9.7,14.3c-2.6,0-4.6-2.1-4.6-4.6s2.1-4.6,4.6-4.6 s4.6,2.1,4.6,4.6S12.3,14.3,9.7,14.3z">
                        </path>
                    </svg>
                </button>

                <button
                    type="button"
                    class="chat_header__icon_button"
                    aria-label="Attach a file"
                    title="Attach a file"
                    :disabled="disabled || isBlocked"
                    @click="openFilePicker"
                >
                    <i class="fa fa-paperclip" aria-hidden="true"></i>
                </button>

                <input
                    ref="fileInput"
                    class="chat_header__file_input"
                    type="file"
                    :accept="acceptedFileTypes"
                    :disabled="disabled || isBlocked"
                    tabindex="-1"
                    @click="$emit('file-click', $event)"
                    @change="$emit('file-change', $event)"
                >

                <button
                    type="button"
                    class="chat_header__icon_button chat_header__pin"
                    :class="{ 'chat_header__pin--active': isPinned }"
                    :aria-label="isPinned ? 'Unpin conversation' : 'Pin conversation'"
                    :aria-pressed="isPinned ? 'true' : 'false'"
                    :title="isPinned ? 'Unpin conversation' : 'Pin conversation'"
                    :disabled="disabled || !roomId"
                    @click="handlePinClick"
                >
                   <i class="fa fa-thumb-tack" aria-hidden="true"></i>
                </button>

                <!-- <button
                    v-if="!isGroupChat"
                    type="button"
                    class="chat_header__block"
                    :class="{ 'chat_header__block--active': isBlocked }"
                    :aria-label="isBlocked ? 'User is blocked' : 'Block user'"
                    :title="isBlocked ? 'User is blocked' : 'Block user'"
                    :disabled="disabled || isBlocked || !roomUserId"
                    @click="handleBlockClick"
                >
                    <svg
                        class="chat_header__block_icon"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path
                            d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 0 1 6.32 12.9L7.1 5.68A7.96 7.96 0 0 1 12 4zM5.68 7.1 16.9 18.32A8 8 0 0 1 5.68 7.1z"
                        />
                    </svg>

                    <span class="chat_header__block_text">
                        {{ isBlocked ? 'Blocked' : 'Block' }}
                    </span>
                </button> -->
            </div>
        </div>
    </header>
</template>

<script>
function firstValue(values) {
    return values.find((value) => {
        return (
            value !== undefined &&
            value !== null &&
            String(value).trim() !== ''
        );
    });
}

function toBoolean(value) {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value === 1;
    }

    if (typeof value === 'string') {
        return ['1', 'true', 'yes', 'online'].indexOf(
            value.trim().toLowerCase()
        ) !== -1;
    }

    return false;
}

function getStatusValue(value) {
    if (
        value === undefined ||
        value === null ||
        typeof value === 'boolean'
    ) {
        return '';
    }

    if (typeof value === 'object') {
        return firstValue([
            value.label,
            value.name,
            value.status,
            value.value,
            value.title
        ]) || '';
    }

    return String(value).trim();
}

function normalizePresenceStatus(value) {
    const rawStatus = getStatusValue(value);

    if (!rawStatus) {
        return null;
    }

    const normalizedStatus = String(rawStatus)
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ');

    const statuses = {
        online: {
            key: 'online',
            label: 'Online'
        },
        available: {
            key: 'available',
            label: 'Available'
        },
        busy: {
            key: 'busy',
            label: 'Busy'
        },
        'do not disturb': {
            key: 'busy',
            label: 'Busy'
        },
        dnd: {
            key: 'busy',
            label: 'Busy'
        },
        meeting: {
            key: 'meeting',
            label: 'At a Meeting'
        },
        'at meeting': {
            key: 'meeting',
            label: 'At a Meeting'
        },
        'at a meeting': {
            key: 'meeting',
            label: 'At a Meeting'
        },
        'in a meeting': {
            key: 'meeting',
            label: 'At a Meeting'
        },
        brb: {
            key: 'brb',
            label: 'Be Right Back'
        },
        'be right back': {
            key: 'brb',
            label: 'Be Right Back'
        },
        away: {
            key: 'away',
            label: 'Away'
        },
        lunch: {
            key: 'lunch',
            label: 'Lunch Away'
        },
        'at lunch': {
            key: 'lunch',
            label: 'Lunch Away'
        },
        'lunch away': {
            key: 'lunch',
            label: 'Lunch Away'
        },
        offline: {
            key: 'offline',
            label: 'Offline'
        }
    };

    return statuses[normalizedStatus] || {
        key: 'custom',
        label: String(rawStatus)
    };
}

export default {
    name: 'ChatHeader',

    props: {
        selectedRoomInfo: {
            type: Object,
            default: null
        },

        isMobile: {
            type: Boolean,
            default: false
        },

        fallbackAvatar: {
            type: String,
            default: ''
        },

        acceptedFileTypes: {
            type: String,
            default: ''
        },

        disabled: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            avatarSource: ''
        };
    },

    computed: {
        room() {
            return this.selectedRoomInfo || {};
        },

        roomUser() {
            return (
                this.room.user ||
                {}
            );
        },

        groupInfo() {
            return (
                this.room ||
                {}
            );
        },

        isGroupChat() {
            return Boolean(
                toBoolean(this.room.is_group_chat)
            );
        },

        displayName() {
            if (this.isGroupChat) {
                return String(
                    firstValue([
                        this.room.group_name,
                        this.room.name
                    ]) || 'Group chat'
                );
            }

            const fullName = [
                this.roomUser.first_name,
                this.roomUser.last_name
            ]
                .filter(Boolean)
                .join(' ')
                .trim();

            return String(
                fullName ||
                'Unknown user'
            );
        },

        initials() {
            const words = this.displayName
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2);

            return words
                .map((word) => word.charAt(0).toUpperCase())
                .join('') || '?';
        },

        memberCount() {
            const memberList = [
                this.groupInfo.users
            ].find((value) => Array.isArray(value));

            if (Array.isArray(memberList)) {
                return memberList.length;
            }
        },

        explicitOnlineState() {
            return firstValue([
                this.roomUser.is_online
            ]);
        },

        rawUserStatus() {
            const statusCandidates = [
                this.roomUser.current_status
            ];

            const directStatus = statusCandidates.find((value) => {
                return Boolean(getStatusValue(value));
            });

            if (directStatus !== undefined) {
                return directStatus;
            }

            /*
             * Some APIs return a text status in online_status.
             * Ignore boolean/numeric values because those only indicate
             * whether the user is connected.
             */
            if (typeof this.roomUser.online_status === 'string') {
                const onlineStatus = this.roomUser.online_status
                    .trim()
                    .toLowerCase();

                if (
                    ['true', 'false', '1', '0'].indexOf(
                        onlineStatus
                    ) === -1
                ) {
                    return this.roomUser.online_status;
                }
            }

            return '';
        },

      presenceStatus() {
        const isOnline = toBoolean(
          this.explicitOnlineState
        );

        if (!isOnline) {
          return {
            key: 'offline',
            label: 'Offline'
          };
        }

        const customPresence = normalizePresenceStatus(
          this.rawUserStatus
        );

        // Ignore a stale "Offline" custom status
        if (
          customPresence &&
          customPresence.key !== 'offline'
        ) {
          return customPresence;
        }

        return {
          key: 'online',
          label: 'Online'
        };
      },

        presenceClass() {
            return 'chat_header__presence--' +
                this.presenceStatus.key;
        },

        statusClass() {
            if (this.isGroupChat) {
                return '';
            }

            return 'chat_header__status--' +
                this.presenceStatus.key;
        },

        subtitle() {
            if (this.isGroupChat) {
                if (this.memberCount !== null) {
                    return this.memberCount === 1
                        ? '1 member'
                        : this.memberCount + ' members';
                }

                return 'Group conversation';
            }

            return this.presenceStatus.label;
        },

        roomId() {
            return firstValue([
                this.room.chat_room,
                this.room.room_id,
                this.room.id
            ]);
        },

        roomUserId() {
            return firstValue([
                this.roomUser.id,
                this.roomUser.user_id,
                this.room.user_id
            ]);
        },

        isPinned() {
            return toBoolean(
                firstValue([
                    this.room.pinned,
                    this.room.is_pinned
                ])
            );
        },

        isBlocked() {
            return toBoolean(
                firstValue([
                    this.room.blocked,
                    this.room.is_blocked,
                    this.roomUser.blocked,
                    this.roomUser.is_blocked
                ])
            );
        },

        identityActionLabel() {
            return this.isGroupChat
                ? 'Open ' + this.displayName + ' group information'
                : 'Open ' + this.displayName + ' profile';
        }
    },

    watch: {
        selectedRoomInfo: {
            immediate: true,

            handler() {
                this.avatarSource = this.resolveAvatarSource();
            }
        }
    },

    methods: {
        resolveAvatarSource() {
            const groupInfo = this.groupInfo || {};
            const roomUser = this.roomUser || {};
            const profile = roomUser.profile || {};

            const source = this.isGroupChat
                ? firstValue([
                    groupInfo.group_image
                ])
                : firstValue([
                    profile.thumbnail
                ]);

            return source
                ? String(source)
                : (this.fallbackAvatar || '');
        },

        handleIdentityClick() {
            if (this.isGroupChat) {
                this.$emit('group-info', this.selectedRoomInfo);
                return;
            }
        },

        openFilePicker() {
            if (this.disabled || this.isBlocked) {
                return;
            }

            const input = this.$refs.fileInput;

            if (input) {
                input.click();
            }
        },

        handlePinClick() {
            if (this.disabled || !this.roomId) {
                return;
            }

            this.$emit(
                'pin',
                this.isPinned,
                this.roomId
            );
        },

        handleBlockClick() {
            if (
                this.disabled ||
                this.isBlocked ||
                !this.roomUserId
            ) {
                return;
            }

            this.$emit(
                'block',
                this.roomUserId,
                this.roomUser.first_name || '',
                this.roomUser.last_name || ''
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.chat_header {
    position: relative;
    // z-index: 20;
    width: 100%;
    color: #f8fafc;
    background:#111111;
    // border-bottom: 1px solid rgba(148, 163, 184, 0.1);
    // box-shadow:
    //     0 8px 24px rgba(0, 0, 0, 0.1),
    //     inset 0 -1px 0 rgba(255, 255, 255, 0.01);
}

.chat_header__inner {
    display: flex;
    align-items: center;
    min-height: 58px;
    padding: 8px 14px;
    gap: 10px;
}

.chat_header__identity {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    min-width: 0;
    padding: 0;
    color: inherit;
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-radius: 10px;

    &:focus-visible {
        outline: 2px solid #7dd3fc;
        outline-offset: 3px;
    }
}

.chat_header__avatar {
    position: relative;
    display: inline-flex;
    flex: 0 0 44px;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    overflow: visible;
    color: #e0f2fe;
    background:
        linear-gradient(145deg, #334155, #0f172a);
    border: 2px solid rgba(255, 255, 255, 0.14);
    border-radius: 50%;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: inherit;
    }
}

.chat_header__initials {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.04em;
}



.chat_header__presence {
    position: absolute;
    right: -1px;
    bottom: 0;
    width: 11px;
    height: 11px;
    background: #64748b;
    border: 2px solid #111827;
    border-radius: 50%;

    &--online,
    &--available {
      background-color: #38b124;
      box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.16);
    }

    &--busy {
        background-color: #ee0809;
        box-shadow: 0 0 0 2px rgba(238, 8, 9, 0.16);
    }

    &--meeting {
      background-color: #f66c11;
      box-shadow: 0 0 0 2px rgba(246, 108, 17, 0.16);
    }

    &--brb,
    &--away {
      background-color: #f3d32d;
      box-shadow: 0 0 0 2px rgba(243, 211, 45, 0.16);
    }

    &--lunch {
        background-color: #1483E5;
        box-shadow: 0 0 0 2px rgba(20, 131, 229, 0.16);
    }

    &--custom {
        background: #a78bfa;
        box-shadow: 0 0 0 2px rgba(167, 139, 250, 0.16);
    }
}


.chat_header__details {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
    margin-left: 11px;
}

.chat_header__name {
    overflow: hidden;
    color: #fff;
    font-size: 18px;
    font-weight: normal;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.chat_header__status {
    margin-top: 2px;
    overflow: hidden;
    color: #94a3b8;
    font-size: 12px;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--online,
    &--available {
        color: #86efac;
    }

    &--busy {
        color: #fca5a5;
    }

    &--meeting {
        color: #fca5a5;
    }

    &--brb,
    &--away {
        color: #fde047;
    }

    &--lunch {
        color: #93c5fd;
    }

    &--custom {
        color: #c4b5fd;
    }
}


.chat_header__actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 5px;
}

.chat_header__icon_button,
.chat_header__block {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 35px;
    color: #cbd5e1;
    cursor: pointer;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 10px;
    transition:
        color 0.18s ease,
        background-color 0.18s ease,
        border-color 0.18s ease,
        transform 0.18s ease;

    svg {
        width: 26px;
        height: 26px;
        fill: currentColor;
    }
    .fa{font-size: 24px;color: currentColor;}

    &:hover:not(:disabled) {
        color: #00efd1;
        background: rgba(0, 239, 209, 0.08);
        border-color: rgba(0, 239, 209, 0.1);
        transform: translateY(-1px);
    }

    &:focus-visible {
        outline: 2px solid #7dd3fc;
        outline-offset: 2px;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }
}

.chat_header__icon_button {
    flex: 0 0 40px;
    width: 40px;
    padding: 0;
}

.chat_header__back {
    margin-left: -4px;
}

.chat_header__pin {
    &--active {
        color: #00efd1;
        background: rgba(0, 239, 209, 0.08);
        border-color: rgba(0, 239, 209, 0.1);
    }
}

.chat_header__block {
    min-width: 76px;
    margin-left: 3px;
    padding: 0 12px;
    color: #fca5a5;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    border-color: #ff4338;

    &:hover:not(:disabled) {
        color: #fff;
        background: #dc2626;
        border-color: #ef4444;
    }

    &--active {
        color: #fecaca;
        background: rgba(127, 29, 29, 0.45);
    }
}

.chat_header__block_icon {
    display: none;
}

.chat_header__file_input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

@media only screen and (max-width: 575px) {
    .chat_header__inner {
        min-height: 52px;
        padding: 6px 10px;
        gap: 5px;
    }

    .chat_header__avatar {
        flex-basis: 40px;
        width: 40px;
        height: 40px;
    }

    .chat_header__details {
        margin-left: 8px;
    }

    .chat_header__name {
        font-size: 16px;
    }

    .chat_header__icon_button {
        flex-basis: 36px;
        width: 36px;
        height: 36px;
        border-radius: 9px;

        svg {
            width: 21px;
            height: 21px;
        }
    }

    .chat_header__actions {
        gap: 1px;
    }

    .chat_header__block {
        min-width: 66px;
        height: 36px;
        margin-left: 2px;
        padding: 0 9px;
        font-size: 11px;
    }
}

@media only screen and (max-width: 420px) {
    .chat_header__status {
        display: none;
    }

    .chat_header__block {
        min-width: 36px;
        width: 36px;
        padding: 0;
    }

    .chat_header__block_text {
        display: none;
    }

    .chat_header__block_icon {
        display: block;
        width: 19px !important;
        height: 19px !important;
    }
}

</style>
